import flatten from 'flat';
import { createRoot } from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { getLinksForTranslation } from './components/atoms/Link';
import EmbeddedAuth from './components/organisms/EmbeddedAuth';
import EmbeddedPasswordReset from './components/organisms/EmbeddedPasswordReset';
import './globalStyles/global.scss';
import setupMockApi from './mockApi';
import { getAppQualifier } from './utils/appQualifier';
import { getCdnShopUrl, loadConfig } from './utils/config';
import formatLocale from './utils/formatLocale';
import { NotificationsProvider } from './utils/notifications';
import { preloadAssets } from './utils/preloadAssets';

if (USE_MOCK_API) {
  setupMockApi();
}

(async () => {
  // Get app qualifier from query parameters or injected config
  const urlParams = new URLSearchParams(window.location.search);
  const injectedAppQualifier = window.ayCheckout?.appQualifier;
  const appQualifier = getAppQualifier({ urlParams, injectedAppQualifier });

  // Get injected returning customer
  const returningCustomer = window.ayCheckout?.customer;

  // Prevent css overwrites from loading
  const hideCssOverwrites = urlParams.get('hideCssOverwrites') === 'true';

  const currentPath = window.location.pathname;
  const passwordResetHash = urlParams.get('hash');
  const isPasswordReset = currentPath === '/embedded/account/password-reset' && !!passwordResetHash;
  const isSignIn = ['/embedded/account', '/embedded/account/login', '/embedded/login'].includes(currentPath);
  const isRegister = ['/embedded/account/registration', '/embedded/register'].includes(currentPath);

  const resize = new ResizeObserver(entries => {
    entries.forEach(entry => {
      window.parent.postMessage(
        {
          type: 'resize',
          height: Math.ceil(entry.contentRect.height)
        },
        '*'
      );
    });
  });
  resize.observe(document.body);

  const loadConfigAndApplyCssVars = async () => {
    const { config } = await loadConfig(appQualifier);
    Object.entries(config.styles).forEach(([key, value]) =>
      document.documentElement.style.setProperty(`--${key}`, value)
    );
    return { config };
  };

  const loadOverwritesCss = () => {
    if (hideCssOverwrites) {
      return;
    }
    const overwritesCss = document.createElement('link');
    overwritesCss.rel = 'stylesheet';
    overwritesCss.href = `${getCdnShopUrl(appQualifier)}/overwrites.css`;
    document.head.appendChild(overwritesCss);
  };

  const [{ config }] = await Promise.all([loadConfigAndApplyCssVars(), loadOverwritesCss()]);

  preloadAssets();

  createRoot(document.getElementById('root')!).render(
    <IntlProvider
      messages={flatten(config.translations)}
      locale={formatLocale(config.locale)}
      defaultRichTextElements={{
        b: chunks => <b>{chunks}</b>,
        strong: chunks => <strong>{chunks}</strong>,
        ...getLinksForTranslation()
      }}
    >
      <NotificationsProvider>
        {isSignIn || isRegister ? (
          <EmbeddedAuth
            initialType={isSignIn ? 'signIn' : 'register'}
            returningCustomer={isSignIn ? returningCustomer : undefined}
          />
        ) : isPasswordReset ? (
          <EmbeddedPasswordReset passwordResetHash={passwordResetHash} />
        ) : null}
      </NotificationsProvider>
    </IntlProvider>
  );
})();
